/**
 * PipelineActivityLogDrawerContainer — data, open-state and FILTER wiring for
 * the per-pipeline activity log (SE-5504 AC3, filters by SE-6251).
 *
 * Subscribes to the drawer reactive var (so the toolbar button can open it
 * without prop-drilling), drives `usePipelineActivityTrail` with a BOARD scope
 * only — no `kanbanCardId`, which is exactly what makes this the whole-pipeline
 * feed rather than one card's history — and forks the layout on SCREEN SIZE.
 *
 * The read is skipped while the drawer is closed so a board that is merely
 * rendered issues nothing.
 *
 * ## Filters
 *
 * Chip selections are held here as sets of CHIP KEYS, expanded to enum lists
 * only at the moment they become query variables. Nothing downstream ever sees
 * a row and a filter at the same time, so client-side narrowing is not merely
 * avoided — there is nowhere to write it.
 *
 * Two mechanics worth naming:
 *
 * - **Press handlers are pre-built** into a key→handler map. A chip row is a
 *   `map` over options, and `react-perf` forbids creating the closure inside
 *   it; this is the `filterHandlers` shape `ReviewQueueDrawerContainer` uses.
 * - **Closing clears the selection**, and it does so in the close HANDLER, not
 *   an effect. `react-hooks/set-state-in-effect` forbids the effect version,
 *   and an event is the honest place for it anyway: reopening the log — very
 *   possibly on a different pipeline — should not silently inherit a filter the
 *   user set minutes ago and can no longer see.
 * @remarks Container component — owns queries, actions, and the breakpoint.
 */

import { useCallback, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import useMobileScreen from "@/hooks/useMobileScreen";

import { usePipelineActivityLogDrawerState } from "../../../hooks/usePipelineActivityLogDrawerState";
import { usePipelineActivityTrail } from "../../../hooks/usePipelineActivityTrail";
import {
  ACTION_FILTER_CHIPS,
  ACTOR_FILTER_CHIPS,
  ALL_FILTER_CHIP,
  expandFilterChips,
  toggleFilterChip,
} from "../../../utils/pipelineActivityFilters";
import PipelineActivityLogDrawerView from "./PipelineActivityLogDrawerView";

/** Stable empty selection — a fresh `new Set()` would churn every render. */
const NO_SELECTION: ReadonlySet<string> = new Set<string>();

/**
 * Stable selection shown while NOTHING is filtered — the "All" chip lit and no
 * action chip lit. Derived state, never stored: "All" is on exactly when both
 * real selections are empty, which is the state that omits both arguments from
 * the request, so the chip cannot drift out of step with the query.
 */
const ALL_SELECTED: ReadonlySet<string> = new Set([ALL_FILTER_CHIP.key]);

/** Action row chips: "All" leads, then the real action bundles. */
const ACTION_ROW_CHIPS = [ALL_FILTER_CHIP, ...ACTION_FILTER_CHIPS];

/**
 * Build the per-chip press handlers for one filter group.
 * @remarks
 * Hoisted to module scope rather than inlined in the `useMemo`. Inline, the
 * shape was `useMemo` → `map` → returned handler → `setState` updater, which is
 * five levels of nested function and trips SonarCloud's nesting-depth rule. It
 * also had to be written twice, once per group. Taking the setter as a
 * parameter collapses both problems.
 * @param chips - The chips in the group.
 * @param setSelected - That group's selection setter.
 * @returns A key→handler map for the group.
 */
const buildChipHandlers = (
  chips: readonly { readonly key: string }[],
  setSelected: Dispatch<SetStateAction<ReadonlySet<string>>>
): Record<string, () => void> =>
  Object.fromEntries(
    chips.map(chip => [
      chip.key,
      () => setSelected(current => toggleFilterChip(current, chip.key)),
    ])
  );

/**
 * Container for the pipeline activity log drawer.
 * @returns The wired {@link PipelineActivityLogDrawerView}.
 */
const PipelineActivityLogDrawerContainer = () => {
  const isSmallScreen = useMobileScreen();
  const { state, close } = usePipelineActivityLogDrawerState();

  const [selectedActionKeys, setSelectedActionKeys] =
    useState<ReadonlySet<string>>(NO_SELECTION);
  const [selectedActorKeys, setSelectedActorKeys] =
    useState<ReadonlySet<string>>(NO_SELECTION);

  // Expanded to the enum lists the query takes. Empty stays empty, and the hook
  // turns an empty list into an OMITTED argument — which is what makes
  // "deselect everything" mean "no filter" rather than "filter to nothing".
  const actions = useMemo(
    () => expandFilterChips(ACTION_FILTER_CHIPS, selectedActionKeys),
    [selectedActionKeys]
  );
  const actorTypes = useMemo(
    () => expandFilterChips(ACTOR_FILTER_CHIPS, selectedActorKeys),
    [selectedActorKeys]
  );

  const {
    rows,
    totalCount,
    loading,
    isError,
    hasMore,
    isLoadingMore,
    loadMore,
  } = usePipelineActivityTrail({
    kanbanBoardId: state.kanbanBoardId,
    // No card scope: AC3 is the whole pipeline's audit feed.
    actions,
    actorTypes,
    skip: !state.open,
  });

  /** Clears every filter — what the leading "All" chip does. */
  const handleSelectAll = useCallback(() => {
    setSelectedActionKeys(NO_SELECTION);
    setSelectedActorKeys(NO_SELECTION);
  }, []);

  /** Pre-built per-chip press handlers, so the View creates no closures. */
  const chipHandlers = useMemo(
    () => ({
      [ALL_FILTER_CHIP.key]: handleSelectAll,
      ...buildChipHandlers(ACTION_FILTER_CHIPS, setSelectedActionKeys),
      ...buildChipHandlers(ACTOR_FILTER_CHIPS, setSelectedActorKeys),
    }),
    [handleSelectAll]
  );

  const filterGroups = useMemo(
    () => [
      {
        key: "action",
        // "Event", not "Activity": the drawer's own title is now "Activity",
        // and two controls reading the same word on one surface is a puzzle.
        label: "Event",
        chips: ACTION_ROW_CHIPS,
        selectedKeys:
          selectedActionKeys.size === 0 && selectedActorKeys.size === 0
            ? ALL_SELECTED
            : selectedActionKeys,
      },
      {
        key: "actor",
        label: "Actor",
        chips: ACTOR_FILTER_CHIPS,
        selectedKeys: selectedActorKeys,
      },
    ],
    [selectedActionKeys, selectedActorKeys]
  );

  const handleClose = useCallback(() => {
    setSelectedActionKeys(NO_SELECTION);
    setSelectedActorKeys(NO_SELECTION);
    close();
  }, [close]);

  return (
    <PipelineActivityLogDrawerView
      isOpen={state.open}
      onClose={handleClose}
      isSmallScreen={isSmallScreen}
      filterGroups={filterGroups}
      chipHandlers={chipHandlers}
      rows={rows}
      totalCount={totalCount}
      loading={loading}
      isError={isError}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      onLoadMore={loadMore}
    />
  );
};

PipelineActivityLogDrawerContainer.displayName =
  "PipelineActivityLogDrawerContainer";

export default PipelineActivityLogDrawerContainer;
